import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import KoobTooltip from './KoobTooltip';
import cn from '../utils/cn';

type KoobCopyProps = {
  /** Text written to the clipboard. */
  value: string;
  /** Displayed content; defaults to the value in monospace. */
  children?: React.ReactNode;
  /** inline: bare trigger. field: read-only input-style box with the copy icon inside. */
  variant?: 'inline' | 'field';
  /** Extra classes on the trigger button, e.g. text color/size. */
  className?: string;
};

const variantClasses = {
  inline: 'inline-flex max-w-full border-none bg-transparent p-0',
  field:
    'flex w-full justify-between rounded-md border border-solid border-input bg-muted/50 px-3 py-2 font-mono text-sm text-foreground transition-colors hover:bg-muted',
};

/**
 * Click-to-copy trigger: renders its content with a trailing copy icon,
 * copies `value` on click and flips the icon to a green check for a moment.
 */
export default function KoobCopy({
  value,
  children,
  variant = 'inline',
  className,
}: KoobCopyProps) {
  const { t } = useTranslation('misc');
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleCopy = async (event: React.MouseEvent) => {
    // rows and cards behind the trigger can be clickable, or be links
    event.preventDefault();
    event.stopPropagation();
    await navigator.clipboard.writeText(value);
    setCopied(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <KoobTooltip
      tip={copied ? t('copied') : t('copy')}
      variant={copied ? 'success' : 'dark'}
      side="top"
      // the trigger must hug the button, or the tip centers on the row
      className={variant === 'inline' ? 'inline-flex max-w-full' : undefined}
    >
      <button
        type="button"
        onClick={handleCopy}
        className={cn(
          'group cursor-pointer items-center gap-1.5 text-left text-inherit [font:inherit]',
          variantClasses[variant],
          className,
        )}
      >
        {children ?? <span className="truncate font-mono">{value}</span>}
        <i
          className={cn(
            'shrink-0 fa-regular',
            copied
              ? 'fa-check text-green-600'
              : 'fa-copy text-muted-foreground group-hover:text-foreground',
          )}
          aria-hidden
        />
      </button>
    </KoobTooltip>
  );
}
